import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { FilterOption } from '@app/src/pages/ResourceCollection/MrtCollection/mrtCollectionTypes'
import { RiskStatus } from '@app/src/types/resourceExplorer'
import { getRiskStatusTranslationKey, RISK_STATUS_DESC_ORDER } from '@app/src/utils/riskStatusHelpers'
import type { IntlShape } from 'react-intl'

// The snapshot collection, relative to whatever the table filters from: the provider itself (no
// prefix), or a provider reached through another resource ('mappingNodes.provider.').
const RISK_COLLECTION = 'providerTopicalRisks'

const riskCollectionPath = (pathToProvider: string): string => `${pathToProvider}${RISK_COLLECTION}`

export const providerRiskStatusField = (pathToProvider = ''): string =>
  `${riskCollectionPath(pathToProvider)}.riskStatus`

// Paired with the status filter so the level is read off the newest snapshot rather than any the
// provider has ever had. The query engine groups a collection's positive filters by the collection
// path and puts them in a single .Any(), which is what keeps the two on the same snapshot.
const LATEST_SNAPSHOT_ONLY: FilterGroup = {
  name: `${RISK_COLLECTION}.isLatest`,
  filters: [{ value: true, operator: Operators.EqualTo }],
}

/** Whether a group belongs to this filter - so a table that narrows its own rows can keep it out of
 * the narrowing it does against a different source. */
export const isProviderRiskFilterGroup = (group: FilterGroup): boolean => group.name.startsWith(`${RISK_COLLECTION}.`)

/**
 * Adds the isLatest companion to filters that carry a risk status but were built without it - the
 * legacy filter bar emits one group per URL parameter, so it can't pair them itself.
 *
 * Only when a status filter is actually present: on its own, isLatest would drop every provider that
 * has no snapshot at all, rather than filtering nothing.
 */
export const withLatestRiskSnapshot = (filterGroups: FilterGroup[]): FilterGroup[] => {
  const hasStatusFilter = filterGroups.some(group => group.name === providerRiskStatusField())
  const hasLatestFilter = filterGroups.some(group => group.name === LATEST_SNAPSHOT_ONLY.name)
  if (!hasStatusFilter || hasLatestFilter) return filterGroups

  return [...filterGroups, LATEST_SNAPSHOT_ONLY]
}

/** The risk levels, worst first - the order the rest of the UI reads them in. */
export const providerRiskFilterOptions = (formatMessage: IntlShape['formatMessage']): FilterOption[] =>
  RISK_STATUS_DESC_ORDER.filter(riskStatus => riskStatus !== RiskStatus.NotApplicable).map(riskStatus => ({
    value: riskStatus,
    label: formatMessage({ id: getRiskStatusTranslationKey(riskStatus) }),
  }))
